/**
 * AI Assistant drawer — the agent workspace's in-shift helper.
 *
 * Opens from the header's sparkle button as a right-hand panel: an agent asks
 * how to do something in KC3 ("how do I transfer a call?", "why won't this
 * ticket close?") or anything else that comes up, without leaving the queue.
 *
 * Streams from `POST /agent/ai/chat/stream` (SSE). Streaming is not a nicety
 * here — on a CPU-only host a local model emits ~2-5 tokens/sec, so a buffered
 * reply would leave an agent staring at a spinner for a minute mid-call.
 * Tokens appear as they are produced and the run can be stopped.
 *
 * The thread lives in component state only. Persisting it would mean storing
 * whatever an agent typed — plausibly customer details — as a new class of
 * tenant record, with retention and erasure obligations that the AI data model
 * does not cover yet. Session-only is the honest scope.
 *
 * Styling matches the agent workspace exactly: 3px corners, `#3366FF` accent,
 * `#0B1120` ink, hairline slate-200 borders.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { History, Loader2, Mic, Plus, Send, Square, Trash2, User, X, Download, Globe, CheckCircle2, FileText, LayoutGrid, Link2, MessageSquare } from 'lucide-react'

import aiAgentAvatar from '../../assets/ai-agnt.png'
import { api, apiPostStream } from '../../api/client.js'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition.js'
import Select from '../ui/Select.js'


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatFile {
  filename: string
  contentType: string
  bufferBase64: string
  sizeBytes: number
}

interface ChatTurn {
  id: number
  role: 'user' | 'assistant'
  content: string
  /** Set when the turn is an error notice rather than a model reply. */
  failed?: boolean
  /** Files generated by the agent (CSV, PDF, XLSX, JSON). */
  files?: ChatFile[]
  /** Structured metadata returned by the agent (e.g. a scrape result). */
  metadata?: Record<string, unknown>
}

/** A message from the server that has not yet been assigned a local turn id. */
type SavedMessage = Omit<ChatTurn, 'id' | 'failed'>

export interface AiAssistantDrawerProps {
  open: boolean
  onClose: () => void
}

/** A saved chat as listed in the history panel. */
interface SavedChat {
  id: string
  title: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Name used until the first `meta` frame lands. The server is authoritative
 * (AI_NAME), so this only covers the pre-first-reply window.
 */
const DEFAULT_NAME = 'Nikhil'

/** Suggested questions shown when the chat opens. */
const SUGGESTIONS = [
  'How do I log in to the agent portal?',
  'How do I register or create a new agent account?',
  'I forgot my agent password, how do I reset it?',
  'How many open tickets are there?',
  'Show me a summary of today\'s conversations',
]

// ---------------------------------------------------------------------------
// Scrape result card
// ---------------------------------------------------------------------------

interface ScrapeResultData {
  type: 'scrape'
  url: string
  title: string
  scrapedAt: string
  fullSite: boolean
  status: 'success'
  stats: { label: string; value: string }[]
  categories: { title: string; items: string[] }[]
  fileCount: number
}

function isScrapeResultData(value: unknown): value is ScrapeResultData {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return v.type === 'scrape' && typeof v.url === 'string' && Array.isArray(v.categories)
}

function ScrapeResultCard({
  data,
  file,
  onAskAbout,
}: {
  data: ScrapeResultData
  file?: ChatFile | undefined
  onAskAbout?: (title: string, url: string) => void
}): JSX.Element {
  const downloadUrl = file ? URL.createObjectURL(
    new Blob([Uint8Array.from(atob(file.bufferBase64), (c) => c.charCodeAt(0))], { type: file.contentType }),
  ) : undefined

  const displayTitle = data.title || data.url
  const hostname = (() => {
    try {
      return new URL(data.url).hostname.replace(/^www\./, '')
    } catch {
      return data.url
    }
  })()

  return (
    <div className="mt-2 overflow-hidden rounded-[3px] border border-slate-200 bg-white">
      <div className="border-b border-slate-100 bg-[#F8FAFC] px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#3366FF]" />
          <span className="text-[12px] font-semibold text-[#0B1120]">Website content extracted successfully!</span>
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500">
          The content from <span className="text-[#3366FF]">{data.url}</span> has been scraped and processed. Here’s a summary of what was extracted:
        </p>
      </div>

      <div className="p-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF]/10 text-[#3366FF]">
            <Globe className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-semibold text-[#0B1120]">{displayTitle}</p>
            <p className="truncate text-[11px] text-slate-500">{hostname}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-[3px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
            <CheckCircle2 className="h-3 w-3" />
            Scraped
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          {data.stats.map((stat, i) => (
            <div key={i} className="rounded-[3px] border border-slate-100 bg-slate-50 p-2 text-center">
              <p className="text-[14px] font-semibold text-[#0B1120]">{stat.value}</p>
              <p className="text-[10px] text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {data.categories.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Key Content Extracted</p>
            {data.categories.map((cat, i) => (
              <div key={i} className="flex gap-2">
                <div className="mt-0.5 shrink-0 text-[#3366FF]">
                  {cat.title === 'Testimonials' ? <MessageSquare className="h-3.5 w-3.5" /> : cat.title === 'Important Links' ? <Link2 className="h-3.5 w-3.5" /> : <LayoutGrid className="h-3.5 w-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-[#0B1120]">{cat.title}</p>
                  <ul className="mt-0.5 space-y-0.5">
                    {cat.items.map((item, j) => (
                      <li key={j} className="truncate text-[11px] text-slate-600">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3 flex gap-2">
          {file && downloadUrl ? (
            <a
              href={downloadUrl}
              download={file.filename}
              className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[3px] border border-[#3366FF] bg-[#3366FF] px-3 py-1.5 text-[12px] font-semibold !text-white transition-all hover:bg-[#2952CC]"
            >
              <FileText className="h-3.5 w-3.5 shrink-0" />
              View Extracted Content
            </a>
          ) : (
            <span className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[3px] border border-slate-200 bg-slate-100 px-3 py-1.5 text-[12px] font-semibold text-slate-400">
              <FileText className="h-3.5 w-3.5 shrink-0" />
              View Extracted Content
            </span>
          )}
          <button
            onClick={() => onAskAbout?.(data.title, data.url)}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-[#3366FF] transition-all hover:bg-[#3366FF]/[0.03]"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Ask about this website
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Bubble({
  turn,
  streaming,
  name,
  onAskAbout,
}: {
  turn: ChatTurn
  streaming?: boolean
  name: string
  onAskAbout?: (topic: string, url: string) => void
}): JSX.Element {
  const isUser = turn.role === 'user'
  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
      {isUser ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF] text-white">
          <User className="h-3.5 w-3.5" />
        </div>
      ) : (
        <img
          src={aiAgentAvatar}
          alt={name}
          className={`h-7 w-7 shrink-0 rounded-full object-cover ring-1 ${
            turn.failed ? 'ring-red-200' : 'ring-slate-200'
          }`}
        />
      )}
      <div
        className={`max-w-[85%] rounded-[3px] px-3 py-2 text-[13px] leading-relaxed ${
          isUser
            ? 'bg-[#3366FF] text-white'
            : turn.failed
              ? 'border border-red-200 bg-red-50 text-red-700'
              : 'border border-slate-200 bg-white text-slate-800'
        }`}
      >
        {turn.content && !isScrapeResultData(turn.metadata?.scrape) && (
          <div className="whitespace-pre-wrap break-words">{turn.content}</div>
        )}
        {!isUser && isScrapeResultData(turn.metadata?.scrape) && (
          <ScrapeResultCard
            data={turn.metadata!.scrape as ScrapeResultData}
            file={turn.files?.[0]}
            onAskAbout={(title, url) => onAskAbout?.(title, url)}
          />
        )}
        {turn.files && turn.files.length > 0 && !isUser && !isScrapeResultData(turn.metadata?.scrape) && (
          <div className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
            {turn.files.map((file, i) => {
              const blob = new Blob(
                [Uint8Array.from(atob(file.bufferBase64), (c) => c.charCodeAt(0))],
                { type: file.contentType },
              )
              const url = URL.createObjectURL(blob)
              return (
                <a
                  key={i}
                  href={url}
                  download={file.filename}
                  className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] font-medium text-[#3366FF] hover:bg-blue-50"
                >
                  <Download className="h-3 w-3" />
                  {file.filename}
                  <span className="text-slate-400">
                    ({file.sizeBytes > 1024 ? `${(file.sizeBytes / 1024).toFixed(1)} KB` : `${file.sizeBytes} B`})
                  </span>
                </a>
              )
            })}
          </div>
        )}
        {streaming && (
          <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-[#3366FF] align-text-bottom" />
        )}
      </div>
    </div>
  )
}

/**
 * Placeholder shown between sending a question and the first token.
 *
 * The orchestrator emits progress events (agent routing, tool calls) but those
 * are internal plumbing, not something an agent mid-shift should read. A
 * skeleton communicates the same "work is happening" without leaking them.
 */
function ThinkingSkeleton({ name }: { name: string }): JSX.Element {
  return (
    <div className="flex gap-2.5">
      <img
        src={aiAgentAvatar}
        alt={name}
        className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-slate-200"
      />
      <div className="w-[85%] max-w-[85%] rounded-[3px] border border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin text-[#3366FF]" />
          Thinking…
        </div>
        <div className="mt-2 animate-pulse space-y-2">
          <div className="h-2.5 w-[45%] rounded-[3px] bg-slate-200" />
          <div className="h-2.5 w-[80%] rounded-[3px] bg-slate-100" />
          <div className="h-2.5 w-[65%] rounded-[3px] bg-slate-100" />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

export default function AiAssistantDrawer({ open, onClose }: AiAssistantDrawerProps): JSX.Element | null {
  const suggestions = SUGGESTIONS
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamText, setStreamText] = useState('')
  const [model, setModel] = useState<string | null>(null)
  // Server-supplied (AI_NAME). Falls back until the first reply arrives.
  const [name, setName] = useState(DEFAULT_NAME)

  // Saved-chat state. `sessionId` is the row this thread persists to; null
  // until the first exchange is saved.
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [chats, setChats] = useState<SavedChat[]>([])
  const [downloadValue, setDownloadValue] = useState('')

  const handleAskAbout = useCallback((topic: string, url: string) => {
    const question = topic && url
      ? `Read more about ${topic} from ${url}`
      : url
        ? `Read more about this website: ${url}`
        : 'Tell me more about this'
    setInput(question)
    inputRef.current?.focus()
  }, [])

  // Speech-to-text: interim results replace the input so the user sees
  // live transcription; final results append so the user can continue
  // typing after a dictated segment.
  const speechBaseRef = useRef('')
  const { listening, supported: micSupported, toggle: toggleMic, stop: stopMic } = useSpeechRecognition({
    onResult: (transcript, isFinal) => {
      if (isFinal) {
        speechBaseRef.current = speechBaseRef.current + transcript + ' '
        setInput(speechBaseRef.current)
      } else {
        setInput(speechBaseRef.current + transcript)
      }
    },
  })

  const loadChats = useCallback(async (): Promise<void> => {
    try {
      const res = await api.get<{ chats: SavedChat[] }>('/agent/ai/chats')
      setChats(res.chats)
    } catch {
      // History is an aid, not the feature — a failure here must not block chat.
    }
  }, [])

  useEffect(() => {
    if (open) void loadChats()
  }, [open, loadChats])

  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const nextId = useRef(1)

  // Typing effect. Tokens land in `typeTargetRef` as they arrive; `streamText`
  // trails behind it a few characters at a time. A local model emits in bursts
  // (a whole sentence, then a pause), which reads as stuttering without this.
  const typeTargetRef = useRef('')
  const typeShownRef = useRef('')

  useEffect(() => {
    if (!streaming) return
    const id = window.setInterval(() => {
      const target = typeTargetRef.current
      const shown = typeShownRef.current
      if (shown.length >= target.length) return
      // Reveal proportionally to the backlog so the caret never falls far
      // behind a fast model, but still animates on a slow one.
      const behind = target.length - shown.length
      const step = Math.max(2, Math.ceil(behind / 18))
      const next = target.slice(0, shown.length + step)
      typeShownRef.current = next
      setStreamText(next)
    }, 18)
    return () => window.clearInterval(id)
  }, [streaming])

  /**
   * Wait for the typewriter to catch up to the final text before settling the
   * turn, so the bubble doesn't jump from half-typed to complete. Capped: a
   * very long reply must not hold the composer disabled indefinitely.
   */
  const drainTyping = useCallback(async (): Promise<void> => {
    const deadline = Date.now() + 4000
    while (typeShownRef.current.length < typeTargetRef.current.length && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 25))
    }
  }, [])

  // Pin to the newest content as tokens arrive.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns, streamText])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // After a reply finishes, return focus to the composer so the agent can
  // keep typing without re-clicking — but only while the panel stays open.
  // Re-focusing on the initial mount (streaming never started) would steal
  // focus unnecessarily, so this only acts once a reply has actually run.
  useEffect(() => {
    if (open && !streaming && turns.length > 0) {
      inputRef.current?.focus()
    }
  }, [open, streaming, turns.length])

  // Close on Escape, but never mid-generation — Escape should not silently
  // discard a reply the agent is waiting on.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !streaming) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, streaming, onClose])

  // Abandoning the workspace must cancel the generation rather than leave the
  // server pushing tokens into a socket nobody reads.
  useEffect(() => () => abortRef.current?.abort(), [])

  const send = useCallback(
    async (text: string): Promise<void> => {
      const question = text.trim()
      if (!question || streaming) return

      const userTurn: ChatTurn = { id: nextId.current++, role: 'user', content: question }

      stopMic()
      speechBaseRef.current = ''
      setTurns((prev) => [...prev, userTurn])
      setInput('')
      setStreaming(true)
      setStreamText('')
      typeTargetRef.current = ''
      typeShownRef.current = ''

      const controller = new AbortController()
      abortRef.current = controller

      let accumulated = ''
      let finalContent = ''
      let finalFiles: ChatFile[] = []
      let finalMetadata: Record<string, unknown> | undefined = undefined
      let streamSessionId = sessionId
      const settle = (content: string, failed?: boolean, files?: ChatFile[], metadata?: Record<string, unknown>): void => {
        setTurns((prev) => [
          ...prev,
          {
            id: nextId.current++,
            role: 'assistant',
            content,
            ...(failed ? { failed: true } : {}),
            ...(files && files.length > 0 ? { files } : {}),
            ...(metadata ? { metadata } : {}),
          },
        ])
      }

      try {
        const chatHistory = turns
          .filter((m: ChatTurn) => !m.failed && m.content)
          .slice(-8)
          .map((m: ChatTurn) => ({ role: m.role as 'user' | 'assistant', content: m.content }))
        const res = await apiPostStream(
          '/agent/ai/agents/chat/stream',
          { message: question, ...(streamSessionId ? { sessionId: streamSessionId } : {}), ...(chatHistory.length > 0 ? { chatHistory } : {}) },
          controller.signal,
        )
        if (!res.body) throw new Error('Streaming is not supported by this connection')

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let settled = false

        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          const frames = buffer.split('\n\n')
          buffer = frames.pop() ?? ''

          for (const frame of frames) {
            let event = 'message'
            let data = ''
            for (const line of frame.split('\n')) {
              if (line.startsWith('event:')) event = line.slice(6).trim()
              else if (line.startsWith('data:')) data += line.slice(5).trim()
            }
            if (!data) continue
            const payload = JSON.parse(data) as Record<string, unknown>

            if (event === 'meta') {
              if (payload.assistantName) setName(String(payload.assistantName))
              // Only adopt the sessionId from meta if we already had one —
              // the API sends a random UUID even for new chats, which would
              // cause the save to fail with 404 (session doesn't exist yet).
              if (payload.sessionId && streamSessionId) {
                streamSessionId = String(payload.sessionId)
                setSessionId(streamSessionId)
              }
            } else if (event === 'delta') {
              accumulated += String(payload.text ?? '')
              typeTargetRef.current = accumulated
            } else if (event === 'done') {
              if (settled) continue
              if (payload.provider && payload.model) {
                setModel(`${String(payload.provider)}:${String(payload.model)}`)
              }
              finalContent = String(payload.content ?? accumulated)
              if (typeof payload.metadata === 'object' && payload.metadata !== null) {
                finalMetadata = payload.metadata as Record<string, unknown>
              }
              // Extract files from the done event
              if (Array.isArray(payload.files)) {
                finalFiles = (payload.files as Array<Record<string, unknown>>).map((f) => ({
                  filename: String(f.filename ?? ''),
                  contentType: String(f.contentType ?? 'application/octet-stream'),
                  bufferBase64: String(f.bufferBase64 ?? ''),
                  sizeBytes: Number(f.sizeBytes ?? 0),
                })).filter((f) => f.filename && f.bufferBase64)
              }
              typeTargetRef.current = finalContent
              await drainTyping()
              setStreaming(false)
              setStreamText('')
              settle(
                finalContent,
                undefined,
                finalFiles.length > 0 ? finalFiles : undefined,
                finalMetadata,
              )
              settled = true
            } else if (event === 'error') {
              setStreaming(false)
              setStreamText('')
              settle(String(payload.error ?? 'The assistant could not answer.'), true)
              settled = true
            }
          }
        }

        if (!settled) {
          if (accumulated) settle(accumulated)
          else settle('The connection closed before the assistant replied. Please try again.', true)
        }
      } catch (err) {
        if (controller.signal.aborted) {
          if (accumulated) settle(accumulated)
        } else {
          settle(err instanceof Error ? err.message : String(err), true)
        }
      } finally {
        setStreaming(false)
        setStreamText('')
        abortRef.current = null
      }

      // Persist after the exchange settles.
      const saveContent = finalContent || accumulated
      if (saveContent) {
        try {
          // Build the FULL conversation: previous turns + current user
          // question + assistant response. We can't rely on `turns` from
          // the closure because setTurns is async — the user turn we just
          // added isn't in `turns` yet.
          const allMessages = [
            ...turns
              .filter((t: ChatTurn) => !t.failed && t.content)
              .map((t) => ({
                role: t.role,
                content: t.content,
                ...(t.metadata ? { metadata: t.metadata } : {}),
                ...(t.files ? { files: t.files } : {}),
              })),
            { role: 'user' as const, content: question },
            {
              role: 'assistant' as const,
              content: saveContent,
              ...(finalFiles.length > 0 ? { files: finalFiles } : {}),
              ...(finalMetadata ? { metadata: finalMetadata } : {}),
            },
          ]
          const body: Record<string, unknown> = {
            messages: allMessages,
          }
          if (streamSessionId) body.sessionId = streamSessionId
          const res = await api.post<{ id: string }>('/agent/ai/chats', body)
          if (!streamSessionId) {
            streamSessionId = res.id
            setSessionId(res.id)
          }
          void loadChats()
        } catch (err) {
          console.error('[AiAssistantDrawer] Failed to save chat:', err instanceof Error ? err.message : String(err))
        }
      }
    },
    [streaming, turns, sessionId, loadChats, drainTyping],
  )

  /** Start a fresh thread, leaving the saved one intact. */
  function newChat(): void {
    abortRef.current?.abort()
    setTurns([])
    setSessionId(null)
    setStreamText('')
    setHistoryOpen(false)
    void loadChats()
  }

  /** Download the current visible thread in the requested format. */
  const downloadChat = useCallback((format: 'txt' | 'csv' | 'json' | 'md'): void => {
    const visibleTurns = turns.filter((t) => !t.failed && t.content)
    if (visibleTurns.length === 0) return

    const stamp = new Date().toISOString().slice(0, 10)
    const safeName = name.toLowerCase().replace(/\s+/g, '-')
    let blob: Blob
    let filename: string

    if (format === 'txt') {
      const lines = visibleTurns.map((t) => `${t.role === 'user' ? 'You' : name}:\n${t.content}`)
      blob = new Blob([lines.join('\n\n')], { type: 'text/plain' })
      filename = `ai-chat-${safeName}-${stamp}.txt`
    } else if (format === 'md') {
      const lines = [
        `# AI Chat — ${name}`,
        `Date: ${new Date().toLocaleString()}`,
        '',
        ...visibleTurns.map((t) => [`**${t.role === 'user' ? 'You' : name}**`, '', t.content, ''].join('\n')),
      ]
      blob = new Blob([lines.join('\n')], { type: 'text/markdown' })
      filename = `ai-chat-${safeName}-${stamp}.md`
    } else if (format === 'csv') {
      const csvField = (value: string): string => {
        const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
        return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded
      }
      const rows = visibleTurns.map((t) => [t.id, t.role, csvField(t.content)].join(','))
      blob = new Blob([['id,role,content'].concat(rows).join('\n')], { type: 'text/csv' })
      filename = `ai-chat-${safeName}-${stamp}.csv`
    } else {
      const exportData = {
        assistant: name,
        exportedAt: new Date().toISOString(),
        turns: visibleTurns.map(({ id, role, content, metadata, files }) => ({
          id,
          role,
          content,
          ...(metadata ? { metadata } : {}),
          ...(files ? { files } : {}),
        })),
      }
      blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      filename = `ai-chat-${safeName}-${stamp}.json`
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }, [turns, name])

  async function openChat(id: string): Promise<void> {
    try {
      const res = await api.get<{ id: string; messages: SavedMessage[] }>(`/agent/ai/chats/${id}`)
      nextId.current = 1
      setTurns(res.messages.map((m, i) => ({ id: i + 1, ...m })))
      nextId.current = res.messages.length + 1
      setSessionId(res.id)
      setHistoryOpen(false)
    } catch {
      // Falls through: the list refreshes and a stale row disappears.
      void loadChats()
    }
  }

  async function deleteChat(id: string): Promise<void> {
    try {
      await api.delete(`/agent/ai/chats/${id}`)
      // Clear the view too if the open thread is the one being deleted —
      // otherwise it stays on screen and re-saves itself on the next turn.
      if (id === sessionId) newChat()
      void loadChats()
    } catch {
      void loadChats()
    }
  }

  if (!open) return null

  return (
    <>
      {/* Scrim. Click-through is blocked so a stray click can't dial or
          disposition something behind the panel. */}
      <div className="fixed inset-0 z-[60] bg-black/20 transition-opacity" onClick={() => !streaming && onClose()} />

      <aside
        className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-[620px] flex-col border-l border-slate-200 bg-[#F8FAFC] shadow-2xl"
        role="dialog"
        aria-label="AI Assistant"
      >
        {/* Header */}
        <header className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 bg-white px-4 py-3">
          <div className="relative shrink-0">
            <img
              src={aiAgentAvatar}
              alt={name}
              className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200"
            />
            {/* Presence dot — pulses while a reply is being generated so the
                status is legible even if the subtitle is scrolled out of view. */}
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                streaming ? 'animate-pulse bg-[#3366FF]' : 'bg-emerald-500'
              }`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[14px] font-semibold text-[#0B1120]">{name}</h2>
            <p className="truncate text-[11px] text-slate-500">
              {streaming ? 'Thinking…' : model ? `AI Agent · ${model}` : 'AI Agent · Smart, fast, reliable'}
            </p>
          </div>
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className={`flex h-8 w-8 items-center justify-center rounded-[3px] transition-all active:scale-95 ${
              historyOpen ? 'bg-[#3366FF]/10 text-[#3366FF]' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
            }`}
            title="Saved chat history"
            aria-label="Saved chat history"
          >
            <History className="h-4 w-4" />
          </button>
          {turns.length > 0 && !streaming && (
            <Select
              ariaLabel="Download chat"
              size="sm"
              className="w-28"
              leftIcon={<Download />}
              value={downloadValue}
              onChange={(v) => {
                if (v) {
                  downloadChat(v as 'txt' | 'csv' | 'json' | 'md')
                  setDownloadValue('')
                }
              }}
            >
              <option value="" disabled>
                Download
              </option>
              <option value="txt">TXT</option>
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="md">Markdown</option>
            </Select>
          )}
          {turns.length > 0 && !streaming && (
            <button
              onClick={newChat}
              className="flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-700 active:scale-95"
              title="New chat"
              aria-label="New chat"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-700 active:scale-95"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* History */}
        {historyOpen && (
          <div className="max-h-64 shrink-0 overflow-y-auto border-b border-slate-200 bg-white px-3 py-2">
            <p className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Your chats
            </p>
            {chats.length === 0 ? (
              <p className="px-1 py-2 text-[12px] text-slate-400">No saved chats yet.</p>
            ) : (
              <ul className="flex flex-col gap-0.5">
                {chats.map((c) => (
                  <li key={c.id} className="group flex items-center gap-1">
                    <button
                      onClick={() => void openChat(c.id)}
                      className={`min-w-0 flex-1 truncate rounded-[3px] px-2 py-1.5 text-left text-[12px] transition-colors ${
                        c.id === sessionId
                          ? 'bg-[#3366FF]/10 text-[#3366FF]'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                      title={c.title}
                    >
                      {c.title}
                    </button>
                    <button
                      onClick={() => void deleteChat(c.id)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Delete this chat"
                      aria-label={`Delete chat: ${c.title}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Thread */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {turns.length === 0 && !streaming && (
            <div className="pt-6 text-center">
              {/* Introduce the assistant before the first question: who it is,
                  what it is for, and — further down — what it can't do. */}
              <img
                src={aiAgentAvatar}
                alt={name}
                className="mx-auto mb-3 h-20 w-20 rounded-full object-cover ring-1 ring-slate-200"
              />
              <p className="text-[14px] font-semibold text-[#0B1120]">{name}</p>
              <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-[#3366FF]">
                AI Agent · Smart · Fast · Reliable
              </p>
              <p className="mx-auto mt-2 max-w-[300px] text-[12px] leading-relaxed text-slate-500">
                Hi, I'm {name} — your in-shift assistant. I know how KC3 works, so ask me about
                calls, queues, breaks or dispositions, or anything else that comes up while you
                work.
              </p>
              <div className="mt-4 flex flex-col gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => void send(s)}
                    className="rounded-[3px] border border-slate-200 bg-white px-3 py-2 text-left text-[12px] text-slate-700 transition-all hover:border-[#3366FF]/40 hover:bg-[#3366FF]/[0.03] active:scale-[0.99]"
                  >
                    {s}
                  </button>
                ))}
              </div>
              {/* Set expectations before the first question rather than after
                  a disappointing answer. */}
              <p className="mx-auto mt-4 max-w-[300px] text-[11px] leading-relaxed text-slate-400">
                {name} can answer questions, look up data, and use AI agents with tools to help you during your shift.
              </p>
            </div>
          )}

          {turns.map((t) => (
            <Bubble key={t.id} turn={t} name={name} onAskAbout={handleAskAbout} />
          ))}

          {streaming && (
            streamText
              ? (
                <Bubble
                  turn={{ id: -1, role: 'assistant', content: streamText }}
                  streaming
                  name={name}
                  onAskAbout={handleAskAbout}
                />
              )
              : <ThinkingSkeleton name={name} />
          )}
        </div>

        {/* Composer */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-3">
          <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-2.5 py-1.5 transition-all focus-within:border-[#3366FF] focus-within:ring-2 focus-within:ring-[#3366FF]/15">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                // If the user cleared the text manually, reset the speech
                // buffer so the next dictation doesn't prepend old text.
                if (e.target.value === '') speechBaseRef.current = ''
                // Grow with the text, capped so the thread stays visible.
                e.target.style.height = 'auto'
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
              }}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter is a newline — the convention every
                // chat surface uses, so agents don't have to learn a new one.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void send(input)
                }
              }}
              rows={1}
              disabled={streaming}
              placeholder={streaming ? 'Waiting for a reply…' : listening ? 'Listening…' : 'Ask a question…'}
              className="max-h-[120px] flex-1 resize-none bg-transparent py-1 text-[13px] leading-5 text-slate-800 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
            />
            {micSupported && !streaming && (
              <button
                onClick={toggleMic}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] transition-all active:scale-95 ${
                  listening
                    ? 'bg-red-100 text-red-600 animate-pulse'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                }`}
                title={listening ? 'Stop listening' : 'Voice input'}
                aria-label={listening ? 'Stop voice input' : 'Start voice input'}
              >
                <Mic className="h-3.5 w-3.5" />
              </button>
            )}
            {streaming ? (
              <button
                onClick={() => abortRef.current?.abort()}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] bg-slate-100 text-slate-600 transition-all hover:bg-slate-200 active:scale-95"
                title="Stop generating"
              >
                <Square className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                onClick={() => void send(input)}
                disabled={input.trim().length === 0}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] bg-[#3366FF] text-white transition-all hover:bg-[#2952CC] active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                title="Send"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <p className="mt-1.5 px-0.5 text-[10px] text-slate-400">
            AI-generated — verify anything important before acting on it.
          </p>
        </div>
      </aside>
    </>
  )
}
